import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell.js';
import {
  CACHE_BUSTER,
  CACHE_MAX_AGE,
  offlinePersister,
  persistQuery,
} from './lib/offline-cache.js';
import { SessionProvider, useSessionQuery } from './lib/session.js';
import { ThemeProvider } from './lib/theme.js';
import { ToastProvider } from './lib/toast.js';
import { BoardListPage } from './pages/BoardListPage.js';
import { BoardPage } from './pages/BoardPage.js';
import { LoginPage } from './pages/LoginPage.js';

/*
 * Seiten, die beim Start niemand sieht, werden nachgeladen (Akzeptanz A1).
 *
 * Sofort geladen bleiben Anmeldung, Board-Liste und Board — mehr ist der
 * Einstieg nicht. Das Board bleibt bewusst dabei: es nachzuladen kostete beim
 * allerersten Besuch einen zusaetzlichen Weg zum Server, und das Board IST
 * die App.
 *
 * `DndLabPage` ist eine Testseite (SPEC 16) und lag bisher im Buendel jedes
 * Familienmitglieds.
 *
 * Ab dem zweiten Start spielt das ohnehin keine Rolle mehr: der Service
 * Worker legt beim Installieren ALLE gehashten Dateien ab.
 */
const MyTasksPage = lazy(() =>
  import('./pages/MyTasksPage.js').then((m) => ({ default: m.MyTasksPage })),
);
const SearchPage = lazy(() =>
  import('./pages/SearchPage.js').then((m) => ({ default: m.SearchPage })),
);
const ProfilePage = lazy(() =>
  import('./pages/ProfilePage.js').then((m) => ({ default: m.ProfilePage })),
);
const NotificationsPage = lazy(() =>
  import('./pages/NotificationsPage.js').then((m) => ({ default: m.NotificationsPage })),
);
const AdminUsersPage = lazy(() =>
  import('./pages/AdminUsersPage.js').then((m) => ({ default: m.AdminUsersPage })),
);
const DndLabPage = lazy(() =>
  import('./pages/DndLabPage.js').then((m) => ({ default: m.DndLabPage })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Beim Fokuswechsel neu laden: bis der WebSocket in Phase 2 kommt, ist
      // das der billigste Weg zu halbwegs frischen Daten.
      refetchOnWindowFocus: true,
      staleTime: 10_000,
      retry: 1,
    },
  },
});

function Routed() {
  const session = useSessionQuery();

  if (session.isLoading) {
    return <p className="p-6 text-sm text-muted">Lädt …</p>;
  }

  if (!session.data) {
    return <LoginPage />;
  }

  return (
    <SessionProvider user={session.data}>
      <ThemeProvider
        serverChoice={session.data.colorScheme}
        serverThemeId={session.data.themeId}
      >
        <AppShell>
          {/*
            Waehrend ein nachgeladener Teil unterwegs ist, steht dieselbe
            Zeile da wie beim Start — keine leere Seite und kein Skelett, das
            nach einem Fehler aussieht.
          */}
          <Suspense fallback={<p className="p-6 text-sm text-muted">Lädt …</p>}>
            <Routes>
              <Route path="/" element={<BoardListPage />} />
              <Route path="/board/:boardId" element={<BoardPage />} />
              <Route path="/meine-aufgaben" element={<MyTasksPage />} />
              <Route path="/suche" element={<SearchPage />} />
              <Route path="/profil" element={<ProfilePage />} />
              <Route path="/profil/benachrichtigungen" element={<NotificationsPage />} />
              {/* Nicht in der Navigation: Testseite fuer Drag & Drop (SPEC 16). */}
              <Route path="/dnd-labor" element={<DndLabPage />} />
              <Route path="/verwaltung" element={<AdminUsersPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AppShell>
      </ThemeProvider>
    </SessionProvider>
  );
}

export default function App() {
  return (
    /*
     * `PersistQueryClientProvider` statt `QueryClientProvider`: er haelt das
     * erste Rendern an, bis der abgelegte Stand eingespielt ist (SPEC 9.7,
     * Akzeptanz A12). Genau darauf kommt es an — waere die Sitzung beim
     * ersten Durchlauf von `Routed` noch nicht da, blitzte im Flugmodus die
     * Anmeldung auf, bevor das Board erscheint.
     */
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: offlinePersister,
        maxAge: CACHE_MAX_AGE,
        buster: CACHE_BUSTER,
        dehydrateOptions: { shouldDehydrateQuery: persistQuery },
      }}
    >
      <ToastProvider>
        <BrowserRouter>
          <Routed />
        </BrowserRouter>
      </ToastProvider>
    </PersistQueryClientProvider>
  );
}
